import React from 'react';

import { PremiumFeatureGuard } from '@qality/common-components/src/atoms/PremiumFeatureGuard';
import { Table } from '@qality/common-components/src/atoms/Table';
import { TestCaseInCycle } from '@qality/common-model';
import { ArrayUtils } from '@qality/common-utils';

import { useTestCaseFilterContext } from './TestCaseFilter';
import TestCycleDetailsActionsHeader from './TestCycleDetailsActionsHeader';
import TestCycleSorterProvider from './TestCycleSorter/TestCycleSorter.provider';
import { TestCycleTestCasesListTabStyled } from './TestCycleTestCasesListTab.styled';
import { TestCycleTestCasesListTabBanner } from './TestCycleTestCasesListTabBanner';
import './_test-cycle-list-tab.scss';
import { useTestCasesInCycleList } from './testCasesInCycleList/TestCaseInCycleListProvider';
import { TestCasesInCycleTableBody } from './testCasesInCycleList/TestCasesInCycleTableBody';
import {
  TestCasesInCycleSortableTableHeader,
  TestCasesInCycleTableHeader,
} from './testCasesInCycleList/TestCasesInCycleTableHeader';

export default function TestCycleTestCasesListTab(): JSX.Element {
  const { testCycle, testCaseActions, selectedTestCasesIds } =
    useTestCasesInCycleList();

  const { isTestCaseFilterActive, filterTestCaseFunction } =
    useTestCaseFilterContext();

  const handleTestCaseSelection = (testCase: TestCaseInCycle) => {
    const updatedSelectedTestCases = selectedTestCasesIds.includes(testCase.id)
      ? selectedTestCasesIds.filter((testCaseId) => testCaseId !== testCase.id)
      : [...selectedTestCasesIds, testCase.id];

    testCaseActions.selectTestCases(updatedSelectedTestCases);
  };

  const removeCurrentlyDisplayedTestCasesIdsFromCheckboxPool = () => {
    testCaseActions.selectTestCases(
      ArrayUtils.removeCommonItemsFromArray(
        selectedTestCasesIds,
        currentlyDisplayedTestCasesIds
      )
    );
  };

  const addCurrentlyDisplayedTestCasesToCheckboxPool = () => {
    testCaseActions.selectTestCases(
      ArrayUtils.mergeUniqueItemsIntoArray(
        selectedTestCasesIds,
        currentlyDisplayedTestCasesIds
      )
    );
  };

  const addOnlyDisplayedTestCaseIdsToPool = () => {
    testCaseActions.selectTestCases(currentlyDisplayedTestCasesIds);
  };

  const handleAllTestCasesSelection = () => {
    if (isAllCurrentlyDisplayedTestCasesSelected) {
      //filter active and all test displayed test cases are selected -> remove displayed from checkbox pool
      removeCurrentlyDisplayedTestCasesIdsFromCheckboxPool();
    } else if (isTestCaseFilterActive) {
      //filter active and not all test cases are selected -> select all displayed checkboxes
      addCurrentlyDisplayedTestCasesToCheckboxPool();
    } else if (!isAllTestCasesSelected) {
      //filter is not active and not all test cases are selected -> select all checkboxes
      addOnlyDisplayedTestCaseIdsToPool();
    } else {
      //filter is not active and all test cases are selected -> unselect all checkboxes
      testCaseActions.selectTestCases([]);
    }
  };

  const currentlyDisplayedTestCasesIds = testCycle.testCases
    .filter(filterTestCaseFunction)
    .map((testCase) => testCase.id);

  const isAllCurentlyDisplayedTestCasesEmpty =
    currentlyDisplayedTestCasesIds.length === 0;

  const isAllCurrentlyDisplayedTestCasesSelected =
    ArrayUtils.doArraysHaveSameItems(
      currentlyDisplayedTestCasesIds,
      selectedTestCasesIds
    );

  const isAllTestCasesSelected =
    !isAllCurentlyDisplayedTestCasesEmpty &&
    (isTestCaseFilterActive
      ? isAllCurrentlyDisplayedTestCasesSelected
      : selectedTestCasesIds.length === currentlyDisplayedTestCasesIds.length);

  return (
    <TestCycleTestCasesListTabStyled>
      <TestCycleDetailsActionsHeader />
      <TestCycleSorterProvider>
        <Table data-testid='test-cases-in-cycle-table'>
          <PremiumFeatureGuard
            premium={
              <TestCasesInCycleSortableTableHeader
                isAllCurentlyDisplayedTestCasesEmpty={
                  isAllCurentlyDisplayedTestCasesEmpty
                }
                isAllTestCasesSelected={isAllTestCasesSelected}
                onAllTestCasesSelection={handleAllTestCasesSelection}
              />
            }
            free={<TestCasesInCycleTableHeader />}
          />
          <TestCasesInCycleTableBody
            selectedTestCasesIds={selectedTestCasesIds}
            onTestCaseSelection={handleTestCaseSelection}
          />
        </Table>
      </TestCycleSorterProvider>
      <PremiumFeatureGuard free={<TestCycleTestCasesListTabBanner />} />
    </TestCycleTestCasesListTabStyled>
  );
}
